import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import { useTab } from 'contexts/TabContext';
import useTenantCode from 'hooks/useTenantCode';
import {
  PageContainer,
  PageHeader,
  Breadcrumb,
  PageTitle,
  Card,
} from 'styles/common.styles';
import { theme } from 'styles/theme';
import { ActionButtonGroup } from 'components/forms';
import { showAlert } from 'utils/toastBridge';
import {
  SAMPLE_PROCESSES,
  SAMPLE_OPTION_STEPS,
  NODE_KIND_LABEL,
  getSampleProcess,
  getEffectiveSteps,
  getStepNeighbors,
  primaryScreen,
} from 'processMap/sampleProcessCatalog';
import { findMenuByScreenId, menuToTabPayload } from 'processMap/processMenuLookup';

const STORAGE_PREFIX = 'processMapSample';

function loadOptions(tenantCode) {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}|${tenantCode || ''}`);
    if (!raw) return { useMonthlyBudget: true, useProposal: true };
    const parsed = JSON.parse(raw);
    return {
      useMonthlyBudget: parsed.useMonthlyBudget !== false,
      useProposal: parsed.useProposal !== false,
    };
  } catch {
    return { useMonthlyBudget: true, useProposal: true };
  }
}

function saveOptions(tenantCode, next) {
  localStorage.setItem(`${STORAGE_PREFIX}|${tenantCode || ''}`, JSON.stringify(next));
}

class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <PageContainer>
          <PageHeader>
            <PageTitle>업무프로세스맵 (샘플)</PageTitle>
          </PageHeader>
          <Card style={{ padding: 24, lineHeight: 1.6 }}>
            <p>화면을 표시하는 중 오류가 났습니다.</p>
            <pre style={{ whiteSpace: 'pre-wrap', marginTop: 12 }}>
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          </Card>
        </PageContainer>
      );
    }
    return this.props.children;
  }
}

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const ProcessTabs = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
`;

const ProcessTab = styled.button`
  border: 1px solid ${(p) => (p.$active ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$active ? theme.colors.primaryLight : theme.colors.white)};
  color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.text.secondary)};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  cursor: pointer;
`;

const FlowScroll = styled.div`
  display: flex;
  align-items: stretch;
  overflow-x: auto;
  padding: ${theme.spacing.xs} 0;
`;

const StepCard = styled.button`
  flex: 0 0 170px;
  width: 170px;
  text-align: left;
  border: 1px solid ${(p) => (p.$selected ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => {
    if (p.$bypassed) return theme.colors.background.medium;
    if (p.$selected) return theme.colors.primaryLight;
    return theme.colors.white;
  }};
  opacity: ${(p) => (p.$bypassed ? 0.55 : 1)};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm};
  cursor: pointer;
`;

const StepSeq = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  font-weight: 600;
  margin-bottom: 4px;
`;

const StepTitle = styled.div`
  font-size: ${theme.typography.fontSize.md};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 8px;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`;

const Badge = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  padding: 1px 6px;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.text.secondary};
  background: ${theme.colors.white};
`;

const StepMeta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.45;
`;

const ArrowCol = styled.div`
  flex: 0 0 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$skip ? theme.colors.orange : theme.colors.text.tertiary)};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: 700;
`;

const ArrowHint = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: 600;
  color: ${theme.colors.orange};
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 280px) 1fr;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`;

const PanelTitle = styled.h3`
  margin: 0 0 ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const Kv = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: 56px 1fr;
  row-gap: 8px;
  column-gap: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
`;

const Dt = styled.dt`
  color: ${theme.colors.text.tertiary};
  font-weight: 600;
`;

const Dd = styled.dd`
  margin: 0;
  color: ${theme.colors.text.primary};
`;

const ScreenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ScreenRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  width: 100%;
  text-align: left;
  border: 1px solid ${(p) => (p.$primary ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$primary ? theme.colors.primaryLight : theme.colors.white)};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  cursor: pointer;
`;

const ScreenName = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const ScreenSub = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  margin-top: 2px;
`;

const ScreenGo = styled.span`
  flex-shrink: 0;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 700;
  color: ${theme.colors.text.link};
`;

const PathLine = styled.p`
  margin: 0 0 ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const ProcessMapSample = () => {
  const tenantCode = useTenantCode();
  const { user } = useAuth();
  const { openTab } = useTab();
  const firstProcess = SAMPLE_PROCESSES[0];
  const [processId, setProcessId] = useState(firstProcess.id);
  const [selectedStepId, setSelectedStepId] = useState(firstProcess.steps[0].id);
  const [options, setOptions] = useState(() => loadOptions(tenantCode));

  const process = useMemo(() => getSampleProcess(processId), [processId]);

  const bypassed = useMemo(() => {
    const set = new Set();
    if (!options.useMonthlyBudget) set.add(SAMPLE_OPTION_STEPS.MONTHLY_BUDGET);
    if (!options.useProposal) set.add(SAMPLE_OPTION_STEPS.EXPENSE_PROPOSAL);
    return set;
  }, [options]);

  const effectiveSteps = useMemo(
    () => getEffectiveSteps(process, bypassed),
    [process, bypassed]
  );

  const selectedStep = useMemo(
    () => process.steps.find((s) => s.id === selectedStepId) || process.steps[0],
    [process, selectedStepId]
  );

  const neighbors = useMemo(
    () => getStepNeighbors(effectiveSteps, selectedStep?.id),
    [effectiveSteps, selectedStep]
  );

  const pathLabel = useMemo(
    () => effectiveSteps.map((s) => s.label).join(' → '),
    [effectiveSteps]
  );

  const openScreen = useCallback(
    (screenId) => {
      const menu = findMenuByScreenId(screenId);
      const payload = menuToTabPayload(menu);
      if (!payload) {
        showAlert(`연결 화면을 메뉴에서 찾지 못했습니다. (${screenId})`);
        return;
      }
      openTab(payload);
    },
    [openTab]
  );

  const openPrimary = useCallback(
    (step) => {
      const link = primaryScreen(step);
      if (!link) {
        showAlert('이 단계에 연결된 화면이 없습니다.');
        return;
      }
      openScreen(link.screenId);
    },
    [openScreen]
  );

  const patchOptions = useCallback(
    (partial) => {
      setOptions((prev) => {
        const next = { ...prev, ...partial };
        saveOptions(tenantCode, next);
        return next;
      });
    },
    [tenantCode]
  );

  const onSelectProcess = (id) => {
    setProcessId(id);
    const next = getSampleProcess(id);
    setSelectedStepId(next.steps[0].id);
  };

  return (
    <PageContainer style={{ overflow: 'auto' }}>
      <PageHeader>
        <Breadcrumb>
          <span>예산관리</span>
          <span className="separator">›</span>
          <span className="current">업무프로세스맵 (샘플)</span>
        </Breadcrumb>
        <PageTitle>업무프로세스맵 (샘플)</PageTitle>
      </PageHeader>
      <ActionButtonGroup
        extraButtons={[
          {
            id: 'opt-monthly',
            label: options.useMonthlyBudget ? '월별예산 사용' : '월별예산 미사용',
            variant: options.useMonthlyBudget ? 'primary' : 'default',
            onClick: () => patchOptions({ useMonthlyBudget: !options.useMonthlyBudget }),
          },
          {
            id: 'opt-proposal',
            label: options.useProposal ? '지출품의 사용' : '지출품의 미사용',
            variant: options.useProposal ? 'primary' : 'default',
            onClick: () => patchOptions({ useProposal: !options.useProposal }),
          },
          {
            id: 'go-primary',
            label: '대표화면 이동',
            variant: 'success',
            onClick: () => openPrimary(selectedStep),
            disabled: !primaryScreen(selectedStep),
          },
        ]}
      />
      <Body>
        <Hint>
          기관 <strong>{tenantCode || user?.compCd || '-'}</strong> 기준 샘플입니다. 카드는{' '}
          <strong>업무 단계</strong>이고, 아래 목록이 연결된 <strong>프로그램</strong>입니다.
          단계를 고른 뒤 프로그램을 누르면 해당 화면 탭이 열립니다.
        </Hint>
        <ProcessTabs>
          {SAMPLE_PROCESSES.map((item) => (
            <ProcessTab
              key={item.id}
              type="button"
              $active={item.id === processId}
              onClick={() => onSelectProcess(item.id)}
            >
              {item.label}
            </ProcessTab>
          ))}
        </ProcessTabs>
        <Card style={{ padding: 16 }}>
          <PathLine>
            {process.summary} 유효경로: {pathLabel}
          </PathLine>
          <FlowScroll>
            {process.steps.map((step, index) => {
              const skipped = bypassed.has(step.id);
              const next = process.steps[index + 1];
              const nextSkipped = next && bypassed.has(next.id);
              const nextActive = process.steps.slice(index + 1).find((s) => !bypassed.has(s.id));
              const skipJump =
                !skipped &&
                nextSkipped &&
                nextActive &&
                process.steps.indexOf(nextActive) - index > 1;
              return (
                <React.Fragment key={step.id}>
                  <StepCard
                    type="button"
                    $selected={selectedStep?.id === step.id}
                    $bypassed={skipped}
                    onClick={() => setSelectedStepId(step.id)}
                    onDoubleClick={() => openPrimary(step)}
                    title="클릭: 상세 · 더블클릭: 대표화면 이동"
                  >
                    <StepSeq>
                      {skipped ? '미사용' : `단계 ${String(index + 1).padStart(2, '0')}`}
                    </StepSeq>
                    <StepTitle>{step.label}</StepTitle>
                    <BadgeRow>
                      <Badge>{NODE_KIND_LABEL[step.nodeKind] || step.nodeKind}</Badge>
                      {step.optional ? <Badge>옵션</Badge> : <Badge>필수</Badge>}
                      {skipped ? <Badge>bypass</Badge> : null}
                    </BadgeRow>
                    <StepMeta>
                      {step.role}
                      <br />
                      연결 {step.screens.length}개
                    </StepMeta>
                  </StepCard>
                  {index < process.steps.length - 1 ? (
                    <ArrowCol $skip={skipJump}>
                      {skipJump ? <ArrowHint>우회</ArrowHint> : null}
                      →
                    </ArrowCol>
                  ) : null}
                </React.Fragment>
              );
            })}
          </FlowScroll>
        </Card>
        <DetailGrid>
          <Card style={{ padding: 16 }}>
            <PanelTitle>업무 속성</PanelTitle>
            <Kv>
              <Dt>업무</Dt>
              <Dd>{selectedStep.label}</Dd>
              <Dt>역할</Dt>
              <Dd>{selectedStep.role}</Dd>
              <Dt>유형</Dt>
              <Dd>{NODE_KIND_LABEL[selectedStep.nodeKind]}</Dd>
              <Dt>선행</Dt>
              <Dd>
                {neighbors.onPath
                  ? neighbors.prev?.label || '(시작)'
                  : '경로에서 제외됨 (bypass)'}
              </Dd>
              <Dt>후속</Dt>
              <Dd>
                {neighbors.onPath
                  ? neighbors.next?.label || '(종료)'
                  : '경로에서 제외됨 (bypass)'}
              </Dd>
              <Dt>설명</Dt>
              <Dd>{selectedStep.summary}</Dd>
            </Kv>
          </Card>
          <Card style={{ padding: 16 }}>
            <PanelTitle>연결 프로그램</PanelTitle>
            <ScreenList>
              {selectedStep.screens.map((link) => {
                const menu = findMenuByScreenId(link.screenId);
                return (
                  <ScreenRow
                    key={`${link.screenId}-${link.note || ''}`}
                    type="button"
                    $primary={!!link.primary}
                    onClick={() => openScreen(link.screenId)}
                  >
                    <div>
                      <ScreenName>
                        {menu?.label || link.screenId}
                        {link.primary ? ' (대표)' : ''}
                      </ScreenName>
                      <ScreenSub>
                        {link.screenId}
                        {link.note ? ` · ${link.note}` : ''}
                        {menu?.url ? ` · ${menu.url}` : ' · 메뉴 미등록'}
                      </ScreenSub>
                    </div>
                    <ScreenGo>화면 이동 →</ScreenGo>
                  </ScreenRow>
                );
              })}
            </ScreenList>
          </Card>
        </DetailGrid>
      </Body>
    </PageContainer>
  );
};

const ProcessMapSamplePage = () => (
  <MapErrorBoundary>
    <ProcessMapSample />
  </MapErrorBoundary>
);

export default ProcessMapSamplePage;
